import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';

// Hook pour les statistiques du dashboard
export const useDashboardStats = () => {
  const { apiCall } = useApi();
  const [stats, setStats] = useState({
    users: { total: 0, active: 0, inactive: 0, percent: 0 },
    prestataires: { total: 0, active: 0, inactive: 0, percent: 0 },
    missions: { total: 0, inProgress: 0, completed: 0, percent: 0 },
    commissions: { total: 0, percent: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Récupérer les données de différentes sources
      const [clientsData, prestatairesData, missionsData] = await Promise.all([
        apiCall('/clients').catch(() => []),
        apiCall('/prestataires').catch(() => []),
        apiCall('/missions').catch(() => [])
      ]);

      // Calculer les stats clients/utilisateurs
      const clients = Array.isArray(clientsData) ? clientsData : [];
      const activeClients = clients.filter(c => c.is_active || c.user_is_active).length;
      const totalClients = clients.length;

      // Calculer les stats prestataires
      const prestataires = Array.isArray(prestatairesData) ? prestatairesData : [];
      const activePrestataires = prestataires.filter(p => p.is_active || p.user_is_active).length;
      const totalPrestataires = prestataires.length;

      // Calculer les stats missions
      const missions = Array.isArray(missionsData) ? missionsData : [];
      const inProgressMissions = missions.filter(m => m.status === 'assigned' || m.status === 'in_progress').length;
      const completedMissions = missions.filter(m => m.status === 'completed').length;
      const totalMissions = missions.length;

      // Calculer les commissions (estimation basée sur les missions complétées)
      const totalCommissions = missions
        .filter(m => m.status === 'completed')
        .reduce((sum, m) => sum + (parseFloat(m.commission) || parseFloat(m.price) * 0.1 || 0), 0);

      setStats({
        users: {
          total: totalClients,
          active: activeClients,
          inactive: totalClients - activeClients,
          percent: totalClients > 0 ? Math.round((activeClients / totalClients) * 100) : 0
        },
        prestataires: {
          total: totalPrestataires,
          active: activePrestataires,
          inactive: totalPrestataires - activePrestataires,
          percent: totalPrestataires > 0 ? Math.round((activePrestataires / totalPrestataires) * 100) : 0
        },
        missions: {
          total: totalMissions,
          inProgress: inProgressMissions,
          completed: completedMissions,
          percent: totalMissions > 0 ? Math.round((inProgressMissions / totalMissions) * 100) : 0
        },
        commissions: {
          total: totalCommissions,
          percent: 78 // Placeholder - à calculer selon la logique métier
        }
      });

    } catch (err) {
      console.error('❌ Dashboard Stats Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return { stats, loading, error, refetch: fetchStats };
};

// Hook pour les graphiques de missions
export const useMissionChartData = (period = 'month') => {
  const { apiCall } = useApi();
  const [chartData, setChartData] = useState({
    labels: [],
    inProgress: [],
    completed: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChartData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const missions = await apiCall('/missions').catch(() => []);
      const missionsList = Array.isArray(missions) ? missions : [];

      // Grouper par mois
      const monthLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      const currentMonth = new Date().getMonth();
      const labels = [];
      const inProgressData = [];
      const completedData = [];

      // Prendre les 6 derniers mois
      for (let i = 5; i >= 0; i--) {
        const monthIndex = (currentMonth - i + 12) % 12;
        labels.push(monthLabels[monthIndex]);

        // Compter les missions pour ce mois
        const monthMissions = missionsList.filter(m => {
          const missionDate = new Date(m.created_at);
          return missionDate.getMonth() === monthIndex;
        });

        inProgressData.push(monthMissions.filter(m => m.status === 'assigned' || m.status === 'in_progress' || m.status === 'pending').length);
        completedData.push(monthMissions.filter(m => m.status === 'completed').length);
      }

      setChartData({
        labels,
        inProgress: inProgressData,
        completed: completedData
      });

    } catch (err) {
      console.error('❌ Mission Chart Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, period]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return { chartData, loading, error, refetch: fetchChartData };
};

// Hook pour les graphiques d'utilisateurs
export const useUserChartData = (period = 'month') => {
  const { apiCall } = useApi();
  const [chartData, setChartData] = useState({
    labels: [],
    active: [],
    inactive: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChartData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [clients, prestataires] = await Promise.all([
        apiCall('/clients').catch(() => []),
        apiCall('/prestataires').catch(() => [])
      ]);

      const allUsers = [...(Array.isArray(clients) ? clients : []), ...(Array.isArray(prestataires) ? prestataires : [])];

      // Grouper par mois
      const monthLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      const currentMonth = new Date().getMonth();
      const labels = [];
      const activeData = [];
      const inactiveData = [];

      for (let i = 5; i >= 0; i--) {
        const monthIndex = (currentMonth - i + 12) % 12;
        labels.push(monthLabels[monthIndex]);

        const monthUsers = allUsers.filter(u => {
          const userDate = new Date(u.created_at);
          return userDate.getMonth() === monthIndex;
        });

        activeData.push(monthUsers.filter(u => u.is_active || u.user_is_active).length);
        inactiveData.push(monthUsers.filter(u => !u.is_active && !u.user_is_active).length);
      }

      setChartData({
        labels,
        active: activeData,
        inactive: inactiveData
      });

    } catch (err) {
      console.error('❌ User Chart Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, period]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return { chartData, loading, error, refetch: fetchChartData };
};

// Hook pour les graphiques de revenus
export const useRevenueChartData = (period = 'month') => {
  const { apiCall } = useApi();
  const [chartData, setChartData] = useState({
    labels: [],
    paid: [],
    unpaid: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChartData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const paiements = await apiCall('/paiements').catch(() => []);
      const paiementsList = Array.isArray(paiements) ? paiements : [];

      // Grouper par mois
      const monthLabels = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
      const currentMonth = new Date().getMonth();
      const labels = [];
      const paidData = [];
      const unpaidData = [];

      for (let i = 5; i >= 0; i--) {
        const monthIndex = (currentMonth - i + 12) % 12;
        labels.push(monthLabels[monthIndex]);

        const monthPaiements = paiementsList.filter(p => {
          const paiementDate = new Date(p.created_at);
          return paiementDate.getMonth() === monthIndex;
        });

        const paidAmount = monthPaiements
          .filter(p => p.status === 'paid' || p.status === 'completed')
          .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

        const unpaidAmount = monthPaiements
          .filter(p => p.status === 'pending' || p.status === 'failed')
          .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

        paidData.push(paidAmount);
        unpaidData.push(unpaidAmount);
      }

      setChartData({
        labels,
        paid: paidData,
        unpaid: unpaidData
      });

    } catch (err) {
      console.error('❌ Revenue Chart Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, period]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  return { chartData, loading, error, refetch: fetchChartData };
};

// Hook pour les alertes
export const useDashboardAlerts = () => {
  const { apiCall } = useApi();
  const [alerts, setAlerts] = useState({
    signalements: 0,
    paiementsRetard: 0,
    documentsAValider: 0,
    documentsRefuses: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [signalements, documents, paiements] = await Promise.all([
        apiCall('/signalements?status=pending').catch(() => []),
        apiCall('/prestataires').catch(() => []),
        apiCall('/paiements?status=pending').catch(() => [])
      ]);

      // Compter les signalements en attente
      const signalementsCount = Array.isArray(signalements) ? signalements.length : 0;

      // Compter les documents à valider (prestataires avec verification_status = pending)
      const prestataires = Array.isArray(documents) ? documents : [];
      const docsAValider = prestataires.filter(p => p.verification_status === 'pending').length;
      const docsRefuses = prestataires.filter(p => p.verification_status === 'rejected').length;

      // Compter les paiements en retard
      const paiementsList = Array.isArray(paiements) ? paiements : [];
      const paiementsEnRetard = paiementsList.filter(p => {
        const dueDate = new Date(p.due_date);
        return p.status === 'pending' && dueDate < new Date();
      }).length;

      setAlerts({
        signalements: signalementsCount,
        paiementsRetard: paiementsEnRetard,
        documentsAValider: docsAValider,
        documentsRefuses: docsRefuses
      });

    } catch (err) {
      console.error('❌ Dashboard Alerts Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  return { alerts, loading, error, refetch: fetchAlerts };
};
